import { useState, useCallback, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Upload, X, Image, Trash2, Film, User, Camera, Play, Link2, Star, StarOff, Loader2, Plus, Images, Check, Pencil, GripVertical, MessageSquare, Search } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { resolveImageUrl } from "../utils/image";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, rectSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categories = ["Photography", "Social Media Content"];

// ── Sortable Project Card ──
function SortableProjectCard({ project, onDelete, onUpdateCover, editingId, setEditingId }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: project.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 0,
  };
  const isEditing = editingId === project.id;
  const hasMultipleImages = project.images?.length > 1;

  return (
    <div ref={setNodeRef} style={style} className="bg-[#0E0E0E] border border-[#1A1A1A] rounded-lg overflow-hidden group relative">
      {!isEditing ? (
        <>
          <div className="relative aspect-[4/3]">
            <img src={resolveImageUrl(project.image_url)} alt={project.title} className="w-full h-full object-cover" />
            {hasMultipleImages && (
              <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                <Images size={12} /> {project.images.length}
              </div>
            )}
            <div className="absolute top-2 right-2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
              {hasMultipleImages && (
                <button onClick={() => setEditingId(project.id)} title="Change cover photo"
                  className="w-7 h-7 rounded-full bg-[#D4C5B0]/80 hover:bg-[#D4C5B0] text-[#050505] flex items-center justify-center">
                  <Pencil size={12} />
                </button>
              )}
              <button onClick={() => onDelete(project.id)}
                className="w-7 h-7 rounded-full bg-red-500/80 hover:bg-red-500 text-white flex items-center justify-center">
                <X size={14} />
              </button>
            </div>
            {project.featured && (
              <div className="absolute top-2 left-2 group-hover:opacity-0 transition-opacity">
                <Star size={14} className="text-[#D4C5B0] fill-[#D4C5B0]" />
              </div>
            )}
            <div {...attributes} {...listeners}
              className="absolute bottom-2 right-2 w-7 h-7 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity">
              <GripVertical size={14} />
            </div>
          </div>
          <div className="p-3">
            <h3 className="text-[#EDEDED] font-medium text-sm truncate">{project.title}</h3>
            <p className="text-[#555] text-xs">{project.category}{project.client ? ` · ${project.client}` : ""}</p>
          </div>
        </>
      ) : (
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[#EDEDED] font-medium text-sm">Select cover photo</h3>
            <button onClick={() => setEditingId(null)} className="text-[#555] hover:text-[#EDEDED] transition-colors">
              <X size={16} />
            </button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {(project.images || []).map((img, i) => (
              <button key={i} onClick={() => { onUpdateCover(project.id, img, i); setEditingId(null); }}
                className={`relative aspect-square overflow-hidden rounded transition-all ${
                  img === project.image_url ? "ring-2 ring-[#D4C5B0]" : "opacity-60 hover:opacity-100"
                }`}>
                <img src={resolveImageUrl(img)} alt="" className="w-full h-full object-cover" />
                {img === project.image_url && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                    <span className="text-[10px] font-bold text-[#D4C5B0] uppercase tracking-wider bg-black/60 px-2 py-0.5 rounded">Cover</span>
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function extractYouTubeId(url) {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return match ? match[1] : null;
}

// ── Album Creator ──
function AlbumCreator({ onDone }) {
  const [photos, setPhotos] = useState([]); // { id, file, preview, uploading, uploaded, url }
  const [isDragging, setIsDragging] = useState(false);
  const [coverIndex, setCoverIndex] = useState(0);
  const [form, setForm] = useState({
    title: "", category: "Photography", client: "", description: "",
    problem: "", approach: "", outcome: "", featured: false
  });
  const [saving, setSaving] = useState(false);

  const addFiles = useCallback((files) => {
    const images = files.filter(f => f.type.startsWith("image/"));
    if (!images.length) return;
    const newPhotos = images.map(f => ({
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
      file: f, preview: URL.createObjectURL(f),
      uploading: false, uploaded: false, url: null
    }));
    setPhotos(prev => [...prev, ...newPhotos]);
  }, []);

  const removePhoto = (id) => {
    setPhotos(prev => {
      const p = prev.find(x => x.id === id);
      if (p) URL.revokeObjectURL(p.preview);
      return prev.filter(x => x.id !== id);
    });
  };

  const handleDragOver = useCallback((e) => { e.preventDefault(); setIsDragging(true); }, []);
  const handleDragLeave = useCallback((e) => { e.preventDefault(); setIsDragging(false); }, []);
  const handleDrop = useCallback((e) => {
    e.preventDefault(); setIsDragging(false);
    addFiles(Array.from(e.dataTransfer.files));
  }, [addFiles]);

  const saveAlbum = async () => {
    if (!photos.length) { toast.error("Add at least one photo"); return; }
    if (!form.title.trim()) { toast.error("Add a title"); return; }
    setSaving(true);
    try {
      // Upload all photos
      const uploadedUrls = [];
      for (let i = 0; i < photos.length; i++) {
        const photo = photos[i];
        setPhotos(prev => prev.map(p => p.id === photo.id ? { ...p, uploading: true } : p));
        if (photo.url) {
          uploadedUrls.push(photo.url);
        } else {
          const formData = new FormData();
          formData.append("file", photo.file);
          const res = await axios.post(`${API}/upload/image`, formData, {
            headers: { "Content-Type": "multipart/form-data" }
          });
          uploadedUrls.push(res.data.image_url);
        }
        setPhotos(prev => prev.map(p => p.id === photo.id ? { ...p, uploading: false, uploaded: true, url: uploadedUrls[uploadedUrls.length - 1] } : p));
      }

      // Create album as one portfolio project
      const coverUrl = uploadedUrls[coverIndex] || uploadedUrls[0];
      await axios.post(`${API}/portfolio`, {
        title: form.title,
        category: form.category,
        image_url: coverUrl,
        images: uploadedUrls,
        description: form.description || null,
        client: form.client || null,
        problem: form.problem || null,
        approach: form.approach || null,
        outcome: form.outcome || null,
        featured: form.featured,
        media_type: "photo"
      });

      toast.success("Album created!");
      photos.forEach(p => URL.revokeObjectURL(p.preview));
      onDone();
    } catch (e) {
      console.error(e);
      toast.error("Failed to save album");
    } finally { setSaving(false); }
  };

  return (
    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
      className="bg-[#0E0E0E] border border-[#1A1A1A] rounded-xl overflow-hidden mb-8">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-[#EDEDED]">New Album</h3>
          <button onClick={onDone} className="text-[#555] hover:text-[#EDEDED] transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Photos */}
          <div>
            {/* Drop zone */}
            <div onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
              data-testid="album-drop-zone"
              className={`relative border-2 border-dashed rounded-xl p-8 mb-4 transition-all cursor-pointer ${
                isDragging ? "border-[#D4C5B0] bg-[#D4C5B0]/10" : "border-[#262626] hover:border-[#444] bg-[#0A0A0A]"
              }`}>
              <input type="file" multiple accept="image/*"
                onChange={(e) => addFiles(Array.from(e.target.files))}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                data-testid="album-file-input" />
              <div className="text-center">
                <Images className={`w-10 h-10 mx-auto mb-2 ${isDragging ? "text-[#D4C5B0]" : "text-[#444]"}`} />
                <p className="text-[#EDEDED] font-medium">{photos.length ? "Add more photos" : "Drop photos here"}</p>
                <p className="text-[#555] text-xs mt-1">or click to browse</p>
              </div>
            </div>

            {/* Photo thumbnails */}
            {photos.length > 0 && (
              <div className="grid grid-cols-4 gap-2">
                {photos.map((photo, i) => (
                  <div key={photo.id} className="relative aspect-square rounded-lg overflow-hidden bg-[#1A1A1A] group cursor-pointer"
                    onClick={() => setCoverIndex(i)} title="Click to set as cover">
                    <img src={photo.preview} alt="" className={`w-full h-full object-cover transition-all ${i === coverIndex ? "ring-2 ring-[#D4C5B0]" : "opacity-70 group-hover:opacity-100"}`} />
                    {i === coverIndex && (
                      <div className="absolute bottom-1 left-1 bg-[#D4C5B0] text-[#050505] text-[9px] font-bold px-1.5 py-0.5 rounded">COVER</div>
                    )}
                    {photo.uploading && (
                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                        <Loader2 className="w-5 h-5 text-[#D4C5B0] animate-spin" />
                      </div>
                    )}
                    {photo.uploaded && (
                      <div className="absolute top-1 right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                        <Check size={10} className="text-white" />
                      </div>
                    )}
                    <button onClick={(e) => { e.stopPropagation(); removePhoto(photo.id); if (i < coverIndex) setCoverIndex(prev => prev - 1); else if (i === coverIndex) setCoverIndex(0); }}
                      className="absolute top-1 right-1 w-5 h-5 bg-red-500/80 rounded-full items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity flex">
                      <X size={10} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {photos.length > 0 && (
              <p className="text-[#555] text-xs mt-2">{photos.length} photo{photos.length > 1 ? "s" : ""} — click a photo to set it as cover</p>
            )}
          </div>

          {/* Right: Details */}
          <div className="space-y-3">
            <div>
              <label className="block text-[#555] text-xs mb-1.5">Album Title *</label>
              <input type="text" value={form.title} onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))}
                placeholder="e.g. D1NZ Round 3 Coverage" data-testid="album-title"
                className="w-full bg-[#1A1A1A] border border-[#262626] text-[#EDEDED] px-3 py-2.5 rounded-lg text-sm focus:border-[#D4C5B0] focus:outline-none" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[#555] text-xs mb-1.5">Category</label>
                <select value={form.category} onChange={(e) => setForm(p => ({ ...p, category: e.target.value }))}
                  data-testid="album-category"
                  className="w-full bg-[#1A1A1A] border border-[#262626] text-[#EDEDED] px-3 py-2.5 rounded-lg text-sm">
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[#555] text-xs mb-1.5">Client</label>
                <input type="text" value={form.client} onChange={(e) => setForm(p => ({ ...p, client: e.target.value }))}
                  placeholder="Client name" data-testid="album-client"
                  className="w-full bg-[#1A1A1A] border border-[#262626] text-[#EDEDED] px-3 py-2.5 rounded-lg text-sm focus:border-[#D4C5B0] focus:outline-none" />
              </div>
            </div>
            <div>
              <label className="block text-[#555] text-xs mb-1.5">Description</label>
              <textarea value={form.description} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))}
                placeholder="Brief description of the project" rows={2} data-testid="album-description"
                className="w-full bg-[#1A1A1A] border border-[#262626] text-[#EDEDED] px-3 py-2.5 rounded-lg text-sm resize-none focus:border-[#D4C5B0] focus:outline-none" />
            </div>
            <details className="group">
              <summary className="text-[#D4C5B0] text-xs uppercase tracking-widest cursor-pointer py-1 flex items-center gap-2">
                Case Study Details <span className="text-[#555] group-open:rotate-90 transition-transform">▸</span>
              </summary>
              <div className="space-y-3 pt-2">
                <div>
                  <label className="block text-[#555] text-xs mb-1.5">The Challenge</label>
                  <textarea value={form.problem} onChange={(e) => setForm(p => ({ ...p, problem: e.target.value }))}
                    placeholder="What problem did the client face?" rows={2}
                    className="w-full bg-[#1A1A1A] border border-[#262626] text-[#EDEDED] px-3 py-2.5 rounded-lg text-sm resize-none focus:border-[#D4C5B0] focus:outline-none" />
                </div>
                <div>
                  <label className="block text-[#555] text-xs mb-1.5">Our Approach</label>
                  <textarea value={form.approach} onChange={(e) => setForm(p => ({ ...p, approach: e.target.value }))}
                    placeholder="How did you tackle it?" rows={2}
                    className="w-full bg-[#1A1A1A] border border-[#262626] text-[#EDEDED] px-3 py-2.5 rounded-lg text-sm resize-none focus:border-[#D4C5B0] focus:outline-none" />
                </div>
                <div>
                  <label className="block text-[#555] text-xs mb-1.5">The Outcome</label>
                  <textarea value={form.outcome} onChange={(e) => setForm(p => ({ ...p, outcome: e.target.value }))}
                    placeholder="What was the result?" rows={2}
                    className="w-full bg-[#1A1A1A] border border-[#262626] text-[#EDEDED] px-3 py-2.5 rounded-lg text-sm resize-none focus:border-[#D4C5B0] focus:outline-none" />
                </div>
              </div>
            </details>
            <div className="flex items-center justify-between pt-3 border-t border-[#1A1A1A]">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.featured} onChange={(e) => setForm(p => ({ ...p, featured: e.target.checked }))}
                  className="w-4 h-4 accent-[#D4C5B0]" />
                <span className="text-[#A3A3A3] text-sm">Featured on homepage</span>
              </label>
              <button onClick={saveAlbum} disabled={saving || !photos.length} data-testid="save-album-btn"
                className="flex items-center gap-2 px-5 py-2.5 bg-[#D4C5B0] text-[#050505] text-sm font-medium rounded-lg hover:bg-[#c4b5a0] disabled:opacity-40 transition-colors">
                {saving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
                {saving ? "Saving..." : "Create Album"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ── Photos Tab ──
function PhotosTab() {
  const [showCreator, setShowCreator] = useState(false);
  const [projects, setProjects] = useState([]);
  const [editingId, setEditingId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => { fetchProjects(); }, []);

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API}/portfolio`);
      setProjects(res.data.filter(p => p.media_type !== "video"));
    } catch (e) { console.error(e); }
  };

  const deleteProject = async (id) => {
    try {
      await axios.delete(`${API}/portfolio/${id}`);
      toast.success("Deleted");
      setProjects(prev => prev.filter(p => p.id !== id));
    } catch (e) { toast.error("Failed to delete"); }
  };

  const updateCover = async (id, newCoverUrl, newCoverIndex) => {
    try {
      await axios.put(`${API}/portfolio/${id}`, { image_url: newCoverUrl, coverIndex: newCoverIndex });
      setProjects(prev => prev.map(p => p.id === id ? { ...p, image_url: newCoverUrl, coverIndex: newCoverIndex } : p));
      toast.success("Cover photo updated!");
    } catch (e) { toast.error("Failed to update cover"); }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = projects.findIndex(p => p.id === active.id);
    const newIndex = projects.findIndex(p => p.id === over.id);
    const reordered = arrayMove(projects, oldIndex, newIndex);
    setProjects(reordered);
    try {
      await axios.put(`${API}/portfolio/reorder`, reordered.map((p, i) => ({ id: p.id, sort_order: i })));
      toast.success("Order saved");
    } catch (e) { toast.error("Failed to save order"); }
  };

  return (
    <div>
      {!showCreator && (
        <button onClick={() => setShowCreator(true)} data-testid="create-album-btn"
          className="w-full mb-8 border-2 border-dashed border-[#333] hover:border-[#D4C5B0] rounded-xl p-8 transition-all group cursor-pointer bg-[#0A0A0A] hover:bg-[#0E0E0E]">
          <div className="text-center">
            <Plus className="w-10 h-10 mx-auto mb-3 text-[#555] group-hover:text-[#D4C5B0] transition-colors" />
            <p className="text-[#EDEDED] text-lg font-medium">Create New Album</p>
            <p className="text-[#555] text-sm mt-1">Drop photos, add details, publish</p>
          </div>
        </button>
      )}

      <AnimatePresence>
        {showCreator && (
          <AlbumCreator onDone={() => { setShowCreator(false); fetchProjects(); }} />
        )}
      </AnimatePresence>

      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-[#EDEDED]">Your Albums ({projects.length})</h2>
        {projects.length > 1 && (
          <p className="text-[#555] text-xs flex items-center gap-1"><GripVertical size={12} /> Drag to reorder</p>
        )}
      </div>
      {projects.length === 0 ? (
        <div className="text-center py-16 bg-[#0E0E0E] border border-[#1A1A1A] rounded-xl">
          <Camera className="w-12 h-12 mx-auto mb-4 text-[#333]" />
          <p className="text-[#555]">No albums yet. Create one above!</p>
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={projects.map(p => p.id)} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {projects.map((p) => (
                <SortableProjectCard key={p.id} project={p} onDelete={deleteProject} onUpdateCover={updateCover} editingId={editingId} setEditingId={setEditingId} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}

// ── Video Tab ──
function SortableVideoCard({ project, onDelete, onChangeThumbnail, onEdit, isEditing, editForm, setEditForm, onSaveEdit, onCancelEdit }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: project.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="bg-[#0E0E0E] border border-[#1A1A1A] rounded-lg overflow-hidden group">
      <div className="relative aspect-video">
        <img src={resolveImageUrl(project.image_url)} alt={project.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-12 h-12 bg-[#D4C5B0]/80 rounded-full flex items-center justify-center">
            <Play size={20} className="text-[#050505] ml-0.5" />
          </div>
        </div>
        <div className="absolute top-2 right-2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <label className="w-7 h-7 rounded-full bg-[#D4C5B0]/80 hover:bg-[#D4C5B0] text-[#050505] flex items-center justify-center cursor-pointer" title="Change thumbnail">
            <Image size={14} />
            <input type="file" accept="image/*" className="hidden"
              onChange={(e) => { if (e.target.files[0]) onChangeThumbnail(project.id, e.target.files[0]); }} />
          </label>
          <button onClick={() => onEdit(project)} title="Edit details"
            className="w-7 h-7 rounded-full bg-[#D4C5B0]/80 hover:bg-[#D4C5B0] text-[#050505] flex items-center justify-center">
            <Pencil size={12} />
          </button>
          <button onClick={() => onDelete(project.id)}
            className="w-7 h-7 rounded-full bg-red-500/80 hover:bg-red-500 text-white flex items-center justify-center">
            <X size={14} />
          </button>
        </div>
        <div {...attributes} {...listeners}
          className="absolute bottom-2 right-2 w-7 h-7 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity">
          <GripVertical size={14} />
        </div>
      </div>
      {isEditing ? (
        <div className="p-3 space-y-2 bg-[#0A0A0A]">
          <input type="text" value={editForm.title} onChange={(e) => setEditForm(f => ({ ...f, title: e.target.value }))}
            placeholder="Title" className="w-full bg-[#1A1A1A] border border-[#262626] text-[#EDEDED] px-3 py-1.5 rounded text-sm focus:border-[#D4C5B0] focus:outline-none" />
          <input type="text" value={editForm.client} onChange={(e) => setEditForm(f => ({ ...f, client: e.target.value }))}
            placeholder="Client / Brand" className="w-full bg-[#1A1A1A] border border-[#262626] text-[#EDEDED] px-3 py-1.5 rounded text-sm focus:border-[#D4C5B0] focus:outline-none" />
          <textarea value={editForm.description} onChange={(e) => setEditForm(f => ({ ...f, description: e.target.value }))}
            placeholder="Description" rows={2} className="w-full bg-[#1A1A1A] border border-[#262626] text-[#EDEDED] px-3 py-1.5 rounded text-sm resize-none focus:border-[#D4C5B0] focus:outline-none" />
          <div className="flex gap-2 justify-end">
            <button onClick={onCancelEdit} className="px-3 py-1 text-[#666] hover:text-[#EDEDED] text-xs transition-colors">Cancel</button>
            <button onClick={onSaveEdit} className="px-3 py-1.5 bg-[#D4C5B0] text-[#050505] text-xs font-medium rounded hover:bg-[#c4b5a0] flex items-center gap-1">
              <Check size={12} /> Save
            </button>
          </div>
        </div>
      ) : (
        <div className="p-3">
          <h3 className="text-[#EDEDED] font-medium text-sm truncate">{project.title}</h3>
          <p className="text-[#555] text-xs truncate">{project.client || "No client"}{project.description ? ` · ${project.description.slice(0, 40)}...` : ""}</p>
          {project.featured && <span className="text-[10px] text-[#D4C5B0]">★ Featured</span>}
        </div>
      )}
    </div>
  );
}

function VideosTab() {
  const [videoUrl, setVideoUrl] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [client, setClient] = useState("");
  const [videoCategory, setVideoCategory] = useState("Film");
  const [saving, setSaving] = useState(false);
  const [featured, setFeatured] = useState(false);
  const [projects, setProjects] = useState([]);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", description: "", client: "" });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => { fetchProjects(); }, []);

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API}/portfolio`);
      setProjects(res.data.filter(p => p.media_type === "video"));
    } catch (e) { console.error(e); }
  };

  const handleThumbnail = (files) => {
    const file = Array.isArray(files) ? files[0] : files;
    if (file && file.type?.startsWith("image/")) {
      setThumbnailFile(file);
      setThumbnailPreview(URL.createObjectURL(file));
    }
  };

  const clearThumbnail = () => {
    if (thumbnailPreview) URL.revokeObjectURL(thumbnailPreview);
    setThumbnailFile(null);
    setThumbnailPreview(null);
  };

  const addVideo = async () => {
    const ytId = extractYouTubeId(videoUrl);
    if (!ytId) { toast.error("Paste a valid YouTube link"); return; }
    setSaving(true);
    try {
      let imageUrl = `https://img.youtube.com/vi/${ytId}/maxresdefault.jpg`;

      // Upload custom thumbnail if provided
      if (thumbnailFile) {
        const formData = new FormData();
        formData.append("file", thumbnailFile);
        const uploadRes = await axios.post(`${API}/upload/image`, formData, { headers: { "Content-Type": "multipart/form-data" } });
        imageUrl = uploadRes.data.image_url;
      }

      await axios.post(`${API}/portfolio`, {
        title: title || "Untitled Video",
        category: videoCategory,
        image_url: imageUrl,
        description: description || null,
        client: client || null,
        featured,
        media_type: "video",
        video_url: videoUrl
      });
      toast.success("Video added!");
      setVideoUrl(""); setTitle(""); setDescription(""); setClient(""); setFeatured(false); setVideoCategory("Film");
      clearThumbnail();
      fetchProjects();
    } catch (e) { toast.error("Failed to add video"); }
    finally { setSaving(false); }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && videoUrl && extractYouTubeId(videoUrl)) addVideo();
  };

  const deleteVideo = async (id) => {
    try {
      await axios.delete(`${API}/portfolio/${id}`);
      toast.success("Deleted");
      setProjects(prev => prev.filter(p => p.id !== id));
    } catch (e) { toast.error("Failed to delete"); }
  };

  const changeThumbnail = async (projectId, file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      const uploadRes = await axios.post(`${API}/upload/image`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      await axios.patch(`${API}/portfolio/${projectId}`, { image_url: uploadRes.data.image_url });
      toast.success("Thumbnail updated!");
      fetchProjects();
    } catch (e) { toast.error("Failed to update thumbnail"); }
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setEditForm({ title: p.title || "", description: p.description || "", client: p.client || "" });
  };

  const saveEdit = async () => {
    try {
      await axios.patch(`${API}/portfolio/${editingId}`, editForm);
      toast.success("Updated!");
      setEditingId(null);
      fetchProjects();
    } catch (e) { toast.error("Failed to update"); }
  };

  const cancelEdit = () => { setEditingId(null); };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = projects.findIndex(p => p.id === active.id);
    const newIndex = projects.findIndex(p => p.id === over.id);
    const reordered = arrayMove(projects, oldIndex, newIndex);
    setProjects(reordered);
    try {
      await axios.put(`${API}/portfolio/reorder`, reordered.map((p, i) => ({ id: p.id, sort_order: i })));
      toast.success("Order saved");
    } catch (e) { toast.error("Failed to save order"); }
  };

  const ytId = extractYouTubeId(videoUrl);

  return (
    <div>
      {/* Quick Add */}
      <div className="bg-[#0E0E0E] border border-[#1A1A1A] rounded-xl p-6 mb-8" data-testid="video-add-section">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 space-y-3">
            <div className="relative">
              <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#555]" />
              <input type="url" value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)}
                onKeyDown={handleKeyDown} placeholder="Paste YouTube link here..." data-testid="video-url-input"
                className="w-full bg-[#1A1A1A] border border-[#262626] text-[#EDEDED] pl-10 pr-4 py-3 rounded-lg text-sm focus:border-[#D4C5B0] focus:outline-none transition-colors" />
            </div>
            {ytId && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input type="text" value={title} onChange={(e) => setTitle(e.target.value)}
                    placeholder="Title" onKeyDown={handleKeyDown} data-testid="video-title-input"
                    className="bg-[#1A1A1A] border border-[#262626] text-[#EDEDED] px-4 py-2.5 rounded-lg text-sm focus:border-[#D4C5B0] focus:outline-none" />
                  <select value={videoCategory} onChange={(e) => setVideoCategory(e.target.value)} data-testid="video-category-select"
                    className="bg-[#1A1A1A] border border-[#262626] text-[#EDEDED] px-4 py-2.5 rounded-lg text-sm focus:border-[#D4C5B0] focus:outline-none">
                    <option value="Film">Film</option>
                    <option value="Social Media Content">Social Media Content</option>
                  </select>
                </div>
                <input type="text" value={client} onChange={(e) => setClient(e.target.value)}
                  placeholder="Client / Brand name" data-testid="video-client-input"
                  className="w-full bg-[#1A1A1A] border border-[#262626] text-[#EDEDED] px-4 py-2.5 rounded-lg text-sm focus:border-[#D4C5B0] focus:outline-none" />
                <textarea value={description} onChange={(e) => setDescription(e.target.value)}
                  placeholder="Short description (shows on project detail page)" rows={2} data-testid="video-description-input"
                  className="w-full bg-[#1A1A1A] border border-[#262626] text-[#EDEDED] px-4 py-2.5 rounded-lg text-sm resize-none focus:border-[#D4C5B0] focus:outline-none" />
                {/* Custom Thumbnail */}
                <div>
                  <label className="block text-[#555] text-xs mb-1.5">Custom Thumbnail</label>
                  {thumbnailPreview ? (
                    <div className="relative w-40 aspect-video rounded-lg overflow-hidden bg-[#1A1A1A]">
                      <img src={thumbnailPreview} alt="Thumbnail" className="w-full h-full object-cover" />
                      <button onClick={clearThumbnail}
                        className="absolute top-1 right-1 w-5 h-5 bg-red-500/80 rounded-full flex items-center justify-center text-white hover:bg-red-500">
                        <X size={10} />
                      </button>
                    </div>
                  ) : (
                    <div className="relative w-40 aspect-video border border-dashed border-[#333] hover:border-[#555] rounded-lg flex items-center justify-center cursor-pointer transition-colors bg-[#0A0A0A]">
                      <input type="file" accept="image/*"
                        onChange={(e) => { if (e.target.files[0]) handleThumbnail(e.target.files[0]); }}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                      <div className="text-center">
                        <Image size={16} className="mx-auto text-[#555] mb-1" />
                        <p className="text-[#555] text-[10px]">Drop or click</p>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={featured} onChange={(e) => setFeatured(e.target.checked)} className="w-4 h-4 accent-[#D4C5B0]" />
                    <span className="text-[#A3A3A3] text-sm">Featured</span>
                  </label>
                  <button onClick={addVideo} disabled={saving} data-testid="add-video-btn"
                    className="ml-auto btn-primary flex items-center gap-2 text-sm disabled:opacity-50">
                    {saving ? <Loader2 size={16} className="animate-spin" /> : <Film size={16} />}
                    {saving ? "Adding..." : "Add Video"}
                  </button>
                </div>
              </motion.div>
            )}
          </div>
          {ytId && (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
              className="w-full md:w-72 aspect-video bg-black rounded-lg overflow-hidden flex-shrink-0">
              <iframe src={`https://www.youtube.com/embed/${ytId}`} title="Preview" className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
            </motion.div>
          )}
        </div>
        {!ytId && videoUrl && <p className="text-red-400 text-xs mt-2">Not a valid YouTube link</p>}
      </div>

      {/* Video Grid */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-[#EDEDED]">Your Videos ({projects.length})</h2>
        {projects.length > 1 && (
          <p className="text-[#555] text-xs flex items-center gap-1"><GripVertical size={12} /> Drag to reorder</p>
        )}
      </div>
      {projects.length === 0 ? (
        <div className="text-center py-16 bg-[#0E0E0E] border border-[#1A1A1A] rounded-xl">
          <Film className="w-12 h-12 mx-auto mb-4 text-[#333]" />
          <p className="text-[#555]">Paste a YouTube link above to get started</p>
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={projects.map(p => p.id)} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {projects.map((p) => (
                <SortableVideoCard key={p.id} project={p} onDelete={deleteVideo} onChangeThumbnail={changeThumbnail}
                  onEdit={startEdit} isEditing={editingId === p.id} editForm={editForm}
                  setEditForm={setEditForm} onSaveEdit={saveEdit} onCancelEdit={cancelEdit} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}

// ── Single Image Slot (for site images) ──
function ImageSlot({ label, currentUrl, onUpload, onClear, testId }) {
  const [isDragging, setIsDragging] = useState(false);
  const handleDragOver = useCallback((e) => { e.preventDefault(); setIsDragging(true); }, []);
  const handleDragLeave = useCallback((e) => { e.preventDefault(); setIsDragging(false); }, []);
  const handleDrop = useCallback((e) => {
    e.preventDefault(); setIsDragging(false);
    const file = Array.from(e.dataTransfer.files).find(f => f.type.startsWith("image/"));
    if (file) onUpload(file);
  }, [onUpload]);

  return (
    <div data-testid={testId} className="bg-[#0E0E0E] border border-[#1A1A1A] rounded-xl p-5">
      <h3 className="text-sm font-semibold text-[#EDEDED] mb-3">{label}</h3>
      {currentUrl ? (
        <div>
          <div className="aspect-[4/3] overflow-hidden bg-[#0A0A0A] rounded-lg mb-3">
            <img src={resolveImageUrl(currentUrl)} alt={label} className="w-full h-full object-cover" />
          </div>
          <div className="flex gap-2">
            <div onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
              className={`relative flex-1 border border-dashed py-2 text-center rounded-lg transition-all cursor-pointer ${
                isDragging ? "border-[#D4C5B0] bg-[#D4C5B0]/10" : "border-[#262626] hover:border-[#555]"
              }`}>
              <input type="file" accept="image/*"
                onChange={(e) => { if (e.target.files[0]) onUpload(e.target.files[0]); }}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
              <span className="text-[#666] text-xs">Replace</span>
            </div>
            <button onClick={onClear} className="px-3 py-1.5 text-red-400/70 hover:text-red-400 text-xs border border-red-400/20 hover:border-red-400/40 rounded-lg transition-colors">
              Reset
            </button>
          </div>
        </div>
      ) : (
        <div onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
          className={`relative aspect-[4/3] border-2 border-dashed flex flex-col items-center justify-center rounded-lg transition-all cursor-pointer ${
            isDragging ? "border-[#D4C5B0] bg-[#D4C5B0]/10" : "border-[#262626] hover:border-[#555]"
          }`}>
          <input type="file" accept="image/*"
            onChange={(e) => { if (e.target.files[0]) onUpload(e.target.files[0]); }}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
          <Image className="w-8 h-8 text-[#333] mb-2" />
          <p className="text-[#555] text-xs">Drop or click</p>
        </div>
      )}
    </div>
  );
}

// ── Site Images Tab ──
function SiteImagesTab() {
  const [settings, setSettings] = useState({ about_hero_image: null, founder_image: null, home_about_image: null, hero_video_url: null, hero_video_file: null, services_photo_image: null, services_video_image: null });
  const [saving, setSaving] = useState(false);
  const [videoInput, setVideoInput] = useState("");
  const [videoUploading, setVideoUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState("");

  useEffect(() => {
    axios.get(`${API}/site-settings`).then(r => {
      setSettings(r.data);
      if (r.data.hero_video_url) setVideoInput(r.data.hero_video_url);
    }).catch(() => {});
  }, []);

  const uploadAndSave = async (file, field) => {
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const uploadRes = await axios.post(`${API}/upload/image`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      await axios.put(`${API}/site-settings`, { [field]: uploadRes.data.image_url });
      setSettings(prev => ({ ...prev, [field]: uploadRes.data.image_url }));
      toast.success("Updated!");
    } catch (e) { toast.error("Failed to update"); }
    finally { setSaving(false); }
  };

  const clearImage = async (field) => {
    try {
      await axios.put(`${API}/site-settings`, { [field]: "" });
      setSettings(prev => ({ ...prev, [field]: null }));
      toast.success("Reset to default");
    } catch (e) { toast.error("Failed to reset"); }
  };

  const saveHeroVideo = async () => {
    if (!videoInput) return;
    const ytId = extractYouTubeId(videoInput);
    if (!ytId) { toast.error("Invalid YouTube link"); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/site-settings`, { hero_video_url: videoInput, hero_video_file: "" });
      setSettings(prev => ({ ...prev, hero_video_url: videoInput, hero_video_file: null }));
      toast.success("Hero video updated!");
    } catch (e) { toast.error("Failed to update"); }
    finally { setSaving(false); }
  };

  const uploadHeroVideo = async (file) => {
    if (!file || !file.type.startsWith("video/")) {
      toast.error("Please select a video file (MP4, MOV, etc.)");
      return;
    }
    if (file.size > 200 * 1024 * 1024) {
      toast.error("Video must be under 200MB");
      return;
    }
    setVideoUploading(true);
    const CHUNK_SIZE = 2 * 1024 * 1024; // 2MB chunks
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

    try {
      // Step 1: Init upload session
      setUploadProgress("Starting upload...");
      const initRes = await axios.post(`${API}/upload/video/init`, {
        filename: file.name,
        total_size: file.size,
        content_type: file.type,
        total_chunks: totalChunks,
      });
      const uploadId = initRes.data.upload_id;

      // Step 2: Upload chunks
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);
        const pct = Math.round(((i + 1) / totalChunks) * 100);
        setUploadProgress(`Uploading... ${pct}%`);

        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("chunk_index", String(i));
        fd.append("file", chunk, file.name);
        await axios.post(`${API}/upload/video/chunk`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 60000,
        });
      }

      // Step 3: Complete upload
      setUploadProgress("Processing video...");
      const completeRes = await axios.post(`${API}/upload/video/complete`, {
        upload_id: uploadId,
      }, { timeout: 300000 });

      // Step 4: Save to site settings
      setUploadProgress("Saving...");
      await axios.put(`${API}/site-settings`, { hero_video_file: completeRes.data.video_url, hero_video_url: "" });
      setSettings(prev => ({ ...prev, hero_video_file: completeRes.data.video_url, hero_video_url: null }));
      setVideoInput("");
      toast.success("Hero video uploaded!");
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Failed to upload video");
    } finally {
      setVideoUploading(false);
      setUploadProgress("");
    }
  };

  const resetHeroVideo = async () => {
    try {
      await axios.put(`${API}/site-settings`, { hero_video_url: "", hero_video_file: "" });
      setSettings(prev => ({ ...prev, hero_video_url: null, hero_video_file: null }));
      setVideoInput("");
      toast.success("Hero video reset to default");
    } catch (e) { toast.error("Failed to reset"); }
  };

  const heroYtId = extractYouTubeId(videoInput);

  return (
    <div>
      <p className="text-[#666] text-sm mb-6">Manage your hero video and site images. Upload a video file directly for the best seamless experience.</p>
      {(saving || videoUploading) && (
        <div className="mb-4 flex items-center gap-2 text-[#D4C5B0] text-sm">
          <Loader2 size={16} className="animate-spin" /> {uploadProgress || "Saving..."}
        </div>
      )}

      {/* Hero Video Section */}
      <div className="bg-[#0E0E0E] border border-[#1A1A1A] rounded-xl p-5 mb-6" data-testid="hero-video-setting">
        <h3 className="text-sm font-semibold text-[#EDEDED] mb-1">Home — Hero Video Banner</h3>
        <p className="text-[#555] text-xs mb-4">Upload your own video for a seamless full-screen background, or paste a YouTube link as fallback.</p>

        {/* Current status */}
        {settings.hero_video_file && (
          <div className="mb-4 p-3 bg-[#0A0A0A] border border-[#1F3A1F] rounded-lg flex items-center gap-3">
            <Film size={18} className="text-green-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-green-400 text-sm font-medium">Direct video active</p>
              <p className="text-[#555] text-xs truncate">Your uploaded video is playing as the hero banner</p>
            </div>
            <button onClick={resetHeroVideo} className="px-3 py-1.5 text-red-400/70 hover:text-red-400 text-xs border border-red-400/20 hover:border-red-400/40 rounded-lg transition-colors flex-shrink-0">
              Remove
            </button>
          </div>
        )}

        {/* Upload video file */}
        <div className="mb-4">
          <label className="block text-[#888] text-xs uppercase tracking-wider mb-2">Upload Video File</label>
          <div className="relative border-2 border-dashed border-[#262626] hover:border-[#D4C5B0] rounded-xl p-6 transition-all cursor-pointer bg-[#0A0A0A] hover:bg-[#0E0E0E] group">
            <input
              type="file"
              accept="video/mp4,video/quicktime,video/webm,video/*"
              onChange={(e) => { if (e.target.files[0]) uploadHeroVideo(e.target.files[0]); }}
              disabled={videoUploading}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
              data-testid="hero-video-file-input"
            />
            <div className="text-center">
              <Upload className="w-8 h-8 mx-auto mb-2 text-[#444] group-hover:text-[#D4C5B0] transition-colors" />
              <p className="text-[#EDEDED] font-medium text-sm">{videoUploading ? "Uploading..." : "Drop your video here or click to browse"}</p>
              <p className="text-[#555] text-xs mt-1">MP4, MOV, WebM — max 200MB</p>
            </div>
          </div>
        </div>

        {/* OR divider */}
        <div className="flex items-center gap-4 mb-4">
          <div className="flex-1 h-px bg-[#1A1A1A]" />
          <span className="text-[#555] text-xs uppercase tracking-wider">or use YouTube</span>
          <div className="flex-1 h-px bg-[#1A1A1A]" />
        </div>

        {/* YouTube fallback */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 space-y-3">
            <div className="flex gap-2">
              <input type="url" value={videoInput} onChange={(e) => setVideoInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") saveHeroVideo(); }}
                placeholder="Paste YouTube link..." data-testid="hero-video-input"
                className="flex-1 bg-[#1A1A1A] border border-[#262626] text-[#EDEDED] px-4 py-2.5 rounded-lg text-sm focus:border-[#D4C5B0] focus:outline-none" />
              <button onClick={saveHeroVideo} disabled={!heroYtId} data-testid="save-hero-video-btn"
                className="px-4 py-2 bg-[#D4C5B0] text-[#050505] text-sm font-medium rounded-lg disabled:opacity-30 hover:bg-[#c4b5a0] transition-colors">Save</button>
            </div>
            {videoInput && !heroYtId && <p className="text-red-400 text-xs">Not a valid YouTube link</p>}
          </div>
          {heroYtId && (
            <div className="w-full md:w-56 aspect-video bg-black rounded-lg overflow-hidden flex-shrink-0">
              <iframe src={`https://www.youtube.com/embed/${heroYtId}`} title="Hero Preview" className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
            </div>
          )}
        </div>
      </div>

      {/* Site Images */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <ImageSlot label="Home — About Section" currentUrl={settings.home_about_image}
          onUpload={(f) => uploadAndSave(f, "home_about_image")} onClear={() => clearImage("home_about_image")} testId="home-about-slot" />
        <ImageSlot label="About Page — Hero" currentUrl={settings.about_hero_image}
          onUpload={(f) => uploadAndSave(f, "about_hero_image")} onClear={() => clearImage("about_hero_image")} testId="about-hero-slot" />
        <ImageSlot label="About Page — Founder" currentUrl={settings.founder_image}
          onUpload={(f) => uploadAndSave(f, "founder_image")} onClear={() => clearImage("founder_image")} testId="founder-image-slot" />
        <ImageSlot label="Services — Photography" currentUrl={settings.services_photo_image}
          onUpload={(f) => uploadAndSave(f, "services_photo_image")} onClear={() => clearImage("services_photo_image")} testId="services-photo-slot" />
        <ImageSlot label="Services — Videography" currentUrl={settings.services_video_image}
          onUpload={(f) => uploadAndSave(f, "services_video_image")} onClear={() => clearImage("services_video_image")} testId="services-video-slot" />
      </div>
    </div>
  );
}

// ── Testimonials Tab ──
function TestimonialsTab() {
  const [testimonials, setTestimonials] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", company: "", role: "", content: "", image_url: null });
  const [saving, setSaving] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  useEffect(() => { fetchTestimonials(); }, []);

  const fetchTestimonials = async () => {
    try {
      const res = await axios.get(`${API}/testimonials`);
      setTestimonials(res.data);
    } catch (e) { console.error(e); }
  };

  const resetForm = () => {
    setForm({ name: "", company: "", role: "", content: "", image_url: null });
    setEditing(null);
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  const startEdit = (t) => {
    setEditing(t.id);
    setForm({ name: t.name, company: t.company, role: t.role, content: t.content, image_url: t.image_url });
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  const handlePhoto = (file) => {
    if (!file?.type?.startsWith("image/")) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const save = async () => {
    if (!form.name.trim() || !form.content.trim()) { toast.error("Name and content are required"); return; }
    setSaving(true);
    try {
      let imageUrl = form.image_url;
      if (photoFile) {
        const fd = new FormData();
        fd.append("file", photoFile);
        const uploadRes = await axios.post(`${API}/upload/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        imageUrl = uploadRes.data.image_url;
      }

      if (editing) {
        await axios.patch(`${API}/testimonials/${editing}`, { ...form, image_url: imageUrl });
        toast.success("Updated!");
      } else {
        await axios.post(`${API}/testimonials`, { ...form, image_url: imageUrl });
        toast.success("Testimonial added!");
      }
      resetForm();
      fetchTestimonials();
    } catch (e) { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const deleteTestimonial = async (id) => {
    try {
      await axios.delete(`${API}/testimonials/${id}`);
      toast.success("Deleted");
      setTestimonials(prev => prev.filter(t => t.id !== id));
    } catch (e) { toast.error("Failed to delete"); }
  };

  return (
    <div>
      {/* Form */}
      <div className="bg-[#0E0E0E] border border-[#1A1A1A] rounded-xl p-6 mb-8" data-testid="testimonial-form">
        <h3 className="text-sm font-semibold text-[#EDEDED] mb-4">{editing ? "Edit Testimonial" : "Add Testimonial"}</h3>
        <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-5">
          {/* Photo */}
          <div className="flex flex-col items-center gap-2">
            {(photoPreview || form.image_url) ? (
              <div className="relative w-20 h-20 rounded-full overflow-hidden bg-[#1A1A1A]">
                <img src={photoPreview || resolveImageUrl(form.image_url)} alt="" className="w-full h-full object-cover" />
                <button onClick={() => { if (photoPreview) URL.revokeObjectURL(photoPreview); setPhotoFile(null); setPhotoPreview(null); setForm(p => ({ ...p, image_url: null })); }}
                  className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500/80 rounded-full flex items-center justify-center text-white">
                  <X size={10} />
                </button>
              </div>
            ) : (
              <div className="relative w-20 h-20 border border-dashed border-[#333] hover:border-[#555] rounded-full flex items-center justify-center cursor-pointer transition-colors bg-[#0A0A0A]">
                <input type="file" accept="image/*" onChange={(e) => { if (e.target.files[0]) handlePhoto(e.target.files[0]); }}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" data-testid="testimonial-photo-input" />
                <User size={20} className="text-[#555]" />
              </div>
            )}
            <span className="text-[#555] text-[10px]">Photo</span>
          </div>
          {/* Fields */}
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <input type="text" value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
                placeholder="Name *" data-testid="testimonial-name"
                className="bg-[#1A1A1A] border border-[#262626] text-[#EDEDED] px-3 py-2.5 rounded-lg text-sm focus:border-[#D4C5B0] focus:outline-none" />
              <input type="text" value={form.company} onChange={(e) => setForm(p => ({ ...p, company: e.target.value }))}
                placeholder="Company" data-testid="testimonial-company"
                className="bg-[#1A1A1A] border border-[#262626] text-[#EDEDED] px-3 py-2.5 rounded-lg text-sm focus:border-[#D4C5B0] focus:outline-none" />
              <input type="text" value={form.role} onChange={(e) => setForm(p => ({ ...p, role: e.target.value }))}
                placeholder="Role / Title" data-testid="testimonial-role"
                className="bg-[#1A1A1A] border border-[#262626] text-[#EDEDED] px-3 py-2.5 rounded-lg text-sm focus:border-[#D4C5B0] focus:outline-none" />
            </div>
            <textarea value={form.content} onChange={(e) => setForm(p => ({ ...p, content: e.target.value }))}
              placeholder="What did they say about your work? *" rows={3} data-testid="testimonial-content"
              className="w-full bg-[#1A1A1A] border border-[#262626] text-[#EDEDED] px-3 py-2.5 rounded-lg text-sm resize-none focus:border-[#D4C5B0] focus:outline-none" />
            <div className="flex gap-2 justify-end">
              {editing && (
                <button onClick={resetForm} className="px-4 py-2 text-[#666] hover:text-[#EDEDED] text-sm transition-colors">Cancel</button>
              )}
              <button onClick={save} disabled={saving || !form.name.trim() || !form.content.trim()} data-testid="save-testimonial-btn"
                className="flex items-center gap-2 px-5 py-2.5 bg-[#D4C5B0] text-[#050505] text-sm font-medium rounded-lg hover:bg-[#c4b5a0] disabled:opacity-40 transition-colors">
                {saving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
                {saving ? "Saving..." : editing ? "Update" : "Add"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* List */}
      <h2 className="text-lg font-semibold text-[#EDEDED] mb-4">Testimonials ({testimonials.length})</h2>
      {testimonials.length === 0 ? (
        <div className="text-center py-16 bg-[#0E0E0E] border border-[#1A1A1A] rounded-xl">
          <MessageSquare className="w-12 h-12 mx-auto mb-4 text-[#333]" />
          <p className="text-[#555]">No testimonials yet. Add one above!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {testimonials.map((t) => (
            <div key={t.id} className="bg-[#0E0E0E] border border-[#1A1A1A] rounded-xl p-5 group" data-testid={`testimonial-${t.id}`}>
              <div className="flex items-start gap-4">
                {t.image_url ? (
                  <img src={resolveImageUrl(t.image_url)} alt={t.name} className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
                ) : (
                  <div className="w-12 h-12 bg-[#1A1A1A] rounded-full flex items-center justify-center flex-shrink-0">
                    <User size={20} className="text-[#555]" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h3 className="text-[#EDEDED] font-medium text-sm">{t.name}</h3>
                      <p className="text-[#555] text-xs">{t.role}{t.company ? ` at ${t.company}` : ""}</p>
                    </div>
                    <div className="flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                      <button onClick={() => startEdit(t)}
                        className="w-7 h-7 rounded-full bg-[#D4C5B0]/20 hover:bg-[#D4C5B0]/40 text-[#D4C5B0] flex items-center justify-center">
                        <Pencil size={12} />
                      </button>
                      <button onClick={() => deleteTestimonial(t.id)}
                        className="w-7 h-7 rounded-full bg-red-500/20 hover:bg-red-500/40 text-red-400 flex items-center justify-center">
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                  <p className="text-[#A3A3A3] text-sm mt-2 leading-relaxed">"{t.content}"</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Content Editor Tab ──
const CONTENT_SECTIONS = [
  // ── HOME PAGE ──
  {
    page: "Home",
    label: "Home — Hero Section",
    fields: [
      { key: "hero_tagline", label: "Tagline", placeholder: "Premium Visual Content", type: "text" },
      { key: "hero_headline", label: "Headline", placeholder: "We Create Visuals That Move Brands Forward.", type: "text" },
      { key: "hero_description", label: "Description", placeholder: "Strategic photography and video for modern businesses...", type: "textarea" },
      { key: "hero_cta_primary", label: "Primary Button", placeholder: "View Our Work", type: "text" },
      { key: "hero_cta_secondary", label: "Secondary Button", placeholder: "Book a Consultation", type: "text" },
    ]
  },
  {
    page: "Home",
    label: "Home — About Section",
    fields: [
      { key: "about_label", label: "Section Label", placeholder: "About OBRA", type: "text" },
      { key: "about_title", label: "Title", placeholder: "More Than Content. We Build Brand Presence.", type: "text" },
      { key: "about_p1", label: "Paragraph 1", placeholder: "OBRA Creatives is the commercial division...", type: "textarea" },
      { key: "about_p2", label: "Paragraph 2", placeholder: "Our approach combines strategic thinking...", type: "textarea" },
    ]
  },
  // ── ABOUT PAGE ──
  {
    page: "About",
    label: "About — Hero",
    fields: [
      { key: "about_hero_label", label: "Label", placeholder: "About Us", type: "text" },
      { key: "about_hero_headline", label: "Headline", placeholder: "More Than Content. We Build Brand Presence.", type: "text" },
      { key: "about_hero_desc", label: "Description", placeholder: "OBRA Creatives exists to bridge the gap between creative excellence and business results...", type: "textarea" },
    ]
  },
  {
    page: "About",
    label: "About — Our Story",
    fields: [
      { key: "about_story_label", label: "Section Label", placeholder: "Our Story", type: "text" },
      { key: "about_story_title", label: "Title", placeholder: "Born from Passion, Driven by Purpose", type: "text" },
      { key: "about_story_p1", label: "Paragraph 1", placeholder: "OBRA Creatives emerged from years of experience in premium visual storytelling...", type: "textarea" },
      { key: "about_story_p2", label: "Paragraph 2", placeholder: "Based in Manawatu, New Zealand, we've had the privilege...", type: "textarea" },
      { key: "about_story_p3", label: "Paragraph 3", placeholder: "Our approach is simple: understand your objectives...", type: "textarea" },
    ]
  },
  {
    page: "About",
    label: "About — Philosophy & Values",
    fields: [
      { key: "about_philosophy_label", label: "Section Label", placeholder: "Our Philosophy", type: "text" },
      { key: "about_philosophy_title", label: "Title", placeholder: "Storytelling Meets Strategy", type: "text" },
      { key: "about_value_1_title", label: "Value 1 — Title", placeholder: "Strategic Focus", type: "text" },
      { key: "about_value_1_desc", label: "Value 1 — Description", placeholder: "Every visual decision is guided by your business objectives...", type: "textarea" },
      { key: "about_value_2_title", label: "Value 2 — Title", placeholder: "Cinematic Quality", type: "text" },
      { key: "about_value_2_desc", label: "Value 2 — Description", placeholder: "We bring film-industry standards to commercial work...", type: "textarea" },
      { key: "about_value_3_title", label: "Value 3 — Title", placeholder: "Creative Innovation", type: "text" },
      { key: "about_value_3_desc", label: "Value 3 — Description", placeholder: "We push boundaries while staying true to your brand...", type: "textarea" },
      { key: "about_value_4_title", label: "Value 4 — Title", placeholder: "True Partnership", type: "text" },
      { key: "about_value_4_desc", label: "Value 4 — Description", placeholder: "We become an extension of your team...", type: "textarea" },
    ]
  },
  {
    page: "About",
    label: "About — Founder",
    fields: [
      { key: "about_founder_label", label: "Section Label", placeholder: "The Founder", type: "text" },
      { key: "about_founder_title", label: "Title", placeholder: "Vision Meets Execution", type: "text" },
      { key: "about_founder_p1", label: "Paragraph 1", placeholder: "With over a decade of experience in visual storytelling...", type: "textarea" },
      { key: "about_founder_p2", label: "Paragraph 2", placeholder: "Having worked with brands ranging from startups...", type: "textarea" },
      { key: "about_founder_quote", label: "Quote", placeholder: "Every brand has a story worth telling...", type: "textarea" },
    ]
  },
  {
    page: "About",
    label: "About — Call to Action",
    fields: [
      { key: "about_cta_title", label: "Title", placeholder: "Ready to Work Together?", type: "text" },
      { key: "about_cta_desc", label: "Description", placeholder: "Let's discuss how we can help elevate your brand...", type: "textarea" },
      { key: "about_cta_btn", label: "Button Text", placeholder: "Start a Conversation", type: "text" },
    ]
  },
  // ── SERVICES PAGE ──
  {
    page: "Services",
    label: "Services — Hero",
    fields: [
      { key: "services_hero_label", label: "Label", placeholder: "Services", type: "text" },
      { key: "services_hero_title", label: "Title", placeholder: "Visual Solutions That Drive Results", type: "text" },
      { key: "services_hero_desc", label: "Description", placeholder: "From single-project shoots to ongoing content partnerships...", type: "textarea" },
    ]
  },
  {
    page: "Services",
    label: "Services — Photography",
    fields: [
      { key: "services_photo_title", label: "Section Title", placeholder: "Commercial Photography", type: "text" },
      { key: "services_photo_desc", label: "Section Description", placeholder: "Strategic photography that captures attention...", type: "textarea" },
      { key: "services_photo_1_title", label: "Service 1 — Title", placeholder: "Branding Shoots", type: "text" },
      { key: "services_photo_1_desc", label: "Service 1 — Description", placeholder: "Comprehensive visual identity packages...", type: "textarea" },
      { key: "services_photo_2_title", label: "Service 2 — Title", placeholder: "Corporate Portraits", type: "text" },
      { key: "services_photo_2_desc", label: "Service 2 — Description", placeholder: "Professional headshots and team photography...", type: "textarea" },
      { key: "services_photo_3_title", label: "Service 3 — Title", placeholder: "Product Photography", type: "text" },
      { key: "services_photo_3_desc", label: "Service 3 — Description", placeholder: "High-impact product imagery...", type: "textarea" },
      { key: "services_photo_4_title", label: "Service 4 — Title", placeholder: "Campaign Visuals", type: "text" },
      { key: "services_photo_4_desc", label: "Service 4 — Description", placeholder: "Strategic campaign photography...", type: "textarea" },
    ]
  },
  {
    page: "Services",
    label: "Services — Videography",
    fields: [
      { key: "services_video_title", label: "Section Title", placeholder: "Commercial Videography", type: "text" },
      { key: "services_video_desc", label: "Section Description", placeholder: "Cinematic video content that tells your story...", type: "textarea" },
      { key: "services_video_1_title", label: "Service 1 — Title", placeholder: "Brand Films", type: "text" },
      { key: "services_video_1_desc", label: "Service 1 — Description", placeholder: "Cinematic storytelling that captures your brand's essence...", type: "textarea" },
      { key: "services_video_2_title", label: "Service 2 — Title", placeholder: "Promotional Videos", type: "text" },
      { key: "services_video_2_desc", label: "Service 2 — Description", placeholder: "Conversion-focused video content...", type: "textarea" },
      { key: "services_video_3_title", label: "Service 3 — Title", placeholder: "Social Media Content", type: "text" },
      { key: "services_video_3_desc", label: "Service 3 — Description", placeholder: "Platform-optimized video content...", type: "textarea" },
      { key: "services_video_4_title", label: "Service 4 — Title", placeholder: "Event Coverage", type: "text" },
      { key: "services_video_4_desc", label: "Service 4 — Description", placeholder: "Professional documentation of corporate events...", type: "textarea" },
    ]
  },
  {
    page: "Services",
    label: "Services — Packages",
    fields: [
      { key: "services_pkg_title", label: "Section Title", placeholder: "Content Subscription Packages", type: "text" },
      { key: "services_pkg_desc", label: "Section Description", placeholder: "Ongoing content creation with predictable pricing...", type: "textarea" },
      { key: "services_pkg_1_name", label: "Package 1 — Name", placeholder: "Starter", type: "text" },
      { key: "services_pkg_1_desc", label: "Package 1 — Description", placeholder: "Perfect for businesses beginning their visual content journey", type: "text" },
      { key: "services_pkg_1_features", label: "Package 1 — Features (one per line)", placeholder: "2 Video reels\n30 secs duration each\n2 hrs video shoot\n20x pro edited photos\nPro equipment included", type: "textarea" },
      { key: "services_pkg_2_name", label: "Package 2 — Name", placeholder: "Growth", type: "text" },
      { key: "services_pkg_2_desc", label: "Package 2 — Description", placeholder: "Ideal for brands ready to scale their content output", type: "text" },
      { key: "services_pkg_2_features", label: "Package 2 — Features (one per line)", placeholder: "4 Video reels\n30 secs duration each\n4 hrs video shoot\n40x pro edited photos\nPro equipment included", type: "textarea" },
      { key: "services_pkg_3_name", label: "Package 3 — Name", placeholder: "Enterprise", type: "text" },
      { key: "services_pkg_3_desc", label: "Package 3 — Description", placeholder: "Comprehensive content partnership for established brands", type: "text" },
      { key: "services_pkg_3_features", label: "Package 3 — Features (one per line)", placeholder: "7 Video reels\n30 secs duration each\n8 hrs video shoot\n80x pro edited photos\nPro equipment included", type: "textarea" },
      { key: "services_notes_title", label: "Notes — Section Title", placeholder: "What's Included", type: "text" },
      { key: "services_note_1_title", label: "Note 1 — Title", placeholder: "Content Creation & Publishing", type: "text" },
      { key: "services_note_1_desc", label: "Note 1 — Text", placeholder: "All packages include professional content creation...", type: "textarea" },
      { key: "services_note_2_title", label: "Note 2 — Title", placeholder: "Platforms", type: "text" },
      { key: "services_note_2_desc", label: "Note 2 — Text", placeholder: "Retainers cover Facebook and Instagram...", type: "textarea" },
      { key: "services_note_3_title", label: "Note 3 — Title", placeholder: "What's Not Included", type: "text" },
      { key: "services_note_3_desc", label: "Note 3 — Text", placeholder: "Community management, replying to comments...", type: "textarea" },
    ]
  },
  {
    page: "Services",
    label: "Services — Process & CTA",
    fields: [
      { key: "services_process_title", label: "Process Title", placeholder: "How We Work", type: "text" },
      { key: "services_step_1_title", label: "Step 1 — Title", placeholder: "Discovery", type: "text" },
      { key: "services_step_1_desc", label: "Step 1 — Description", placeholder: "We learn about your brand, objectives, and vision...", type: "textarea" },
      { key: "services_step_2_title", label: "Step 2 — Title", placeholder: "Strategy", type: "text" },
      { key: "services_step_2_desc", label: "Step 2 — Description", placeholder: "We develop a creative approach tailored to your goals...", type: "textarea" },
      { key: "services_step_3_title", label: "Step 3 — Title", placeholder: "Production", type: "text" },
      { key: "services_step_3_desc", label: "Step 3 — Description", placeholder: "We execute with precision, capturing content...", type: "textarea" },
      { key: "services_step_4_title", label: "Step 4 — Title", placeholder: "Delivery", type: "text" },
      { key: "services_step_4_desc", label: "Step 4 — Description", placeholder: "We refine and deliver polished assets...", type: "textarea" },
      { key: "services_cta_title", label: "CTA Title", placeholder: "Let's Discuss Your Project", type: "text" },
      { key: "services_cta_desc", label: "CTA Description", placeholder: "Every project starts with a conversation...", type: "textarea" },
    ]
  },
  // ── CONTACT PAGE ──
  {
    page: "Contact",
    label: "Contact — Hero & Info",
    fields: [
      { key: "contact_hero_label", label: "Label", placeholder: "Contact", type: "text" },
      { key: "contact_hero_title", label: "Headline", placeholder: "Let's Build Something That Elevates Your Brand.", type: "text" },
      { key: "contact_hero_desc", label: "Description", placeholder: "Ready to create powerful visual content? Get in touch...", type: "textarea" },
      { key: "contact_email", label: "Email Address", placeholder: "create@obracreatives.co.nz", type: "text" },
      { key: "contact_phone", label: "Phone Number", placeholder: "+64 21 029 22558", type: "text" },
      { key: "contact_location", label: "Location", placeholder: "Manawatu, New Zealand", type: "text" },
    ]
  },
  {
    page: "Contact",
    label: "Contact — FAQ",
    fields: [
      { key: "contact_faq_1_q", label: "FAQ 1 — Question", placeholder: "What's your typical turnaround time?", type: "text" },
      { key: "contact_faq_1_a", label: "FAQ 1 — Answer", placeholder: "Project timelines vary based on scope...", type: "textarea" },
      { key: "contact_faq_2_q", label: "FAQ 2 — Question", placeholder: "Do you work with businesses outside your region?", type: "text" },
      { key: "contact_faq_2_a", label: "FAQ 2 — Answer", placeholder: "Absolutely. While we're based in Manawatu...", type: "textarea" },
      { key: "contact_faq_3_q", label: "FAQ 3 — Question", placeholder: "What's included in a typical project?", type: "text" },
      { key: "contact_faq_3_a", label: "FAQ 3 — Answer", placeholder: "Each project includes pre-production planning...", type: "textarea" },
      { key: "contact_faq_4_q", label: "FAQ 4 — Question", placeholder: "Do you offer ongoing content packages?", type: "text" },
      { key: "contact_faq_4_a", label: "FAQ 4 — Answer", placeholder: "Yes, we offer monthly retainer packages...", type: "textarea" },
    ]
  },
];

function ContentTab() {
  const [content, setContent] = useState({});
  const [original, setOriginal] = useState({});
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [activePage, setActivePage] = useState("Home");

  useEffect(() => {
    axios.get(`${API}/site-content`).then(r => {
      setContent(r.data || {});
      setOriginal(r.data || {});
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const hasChanges = JSON.stringify(content) !== JSON.stringify(original);
  const pages = [...new Set(CONTENT_SECTIONS.map(s => s.page))];
  const filteredSections = CONTENT_SECTIONS.filter(s => s.page === activePage);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/site-content`, content);
      setOriginal(res.data || {});
      setContent(res.data || {});
      toast.success("Content saved!");
    } catch (e) { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const resetField = (key) => {
    setContent(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  if (!loaded) return <div className="text-center py-16 text-[#555]"><Loader2 className="w-8 h-8 mx-auto animate-spin" /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="text-[#666] text-sm">Edit the text on your website. Leave fields empty to use defaults.</p>
        {hasChanges && (
          <button onClick={save} disabled={saving} data-testid="save-content-btn"
            className="flex items-center gap-2 px-5 py-2.5 bg-[#D4C5B0] text-[#050505] text-sm font-medium rounded-lg hover:bg-[#c4b5a0] disabled:opacity-50 transition-colors">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
            {saving ? "Saving..." : "Save Changes"}
          </button>
        )}
      </div>

      {/* Page filter tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-1" data-testid="content-page-tabs">
        {pages.map(page => (
          <button key={page} onClick={() => setActivePage(page)}
            data-testid={`content-tab-${page.toLowerCase()}`}
            className={`px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition-colors ${
              activePage === page ? "bg-[#D4C5B0] text-[#050505]" : "bg-[#1A1A1A] text-[#888] hover:text-[#EDEDED]"
            }`}>
            {page}
          </button>
        ))}
      </div>

      <div className="space-y-6">
        {filteredSections.map((section) => (
          <div key={section.label} className="bg-[#0E0E0E] border border-[#1A1A1A] rounded-xl p-6" data-testid={`content-section-${section.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <h3 className="text-sm font-semibold text-[#D4C5B0] uppercase tracking-wider mb-4">{section.label}</h3>
            <div className="space-y-4">
              {section.fields.map((field) => (
                <div key={field.key}>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-[#888] text-xs">{field.label}</label>
                    {content[field.key] && (
                      <button onClick={() => resetField(field.key)}
                        className="text-[#555] hover:text-[#A3A3A3] text-[10px] uppercase tracking-wider transition-colors">
                        Reset to default
                      </button>
                    )}
                  </div>
                  {field.type === "textarea" ? (
                    <textarea
                      value={content[field.key] || ""}
                      onChange={(e) => setContent(prev => ({ ...prev, [field.key]: e.target.value }))}
                      placeholder={field.placeholder}
                      rows={3}
                      data-testid={`content-${field.key}`}
                      className="w-full bg-[#1A1A1A] border border-[#262626] text-[#EDEDED] px-4 py-2.5 rounded-lg text-sm resize-none focus:border-[#D4C5B0] focus:outline-none transition-colors"
                    />
                  ) : (
                    <input
                      type="text"
                      value={content[field.key] || ""}
                      onChange={(e) => setContent(prev => ({ ...prev, [field.key]: e.target.value }))}
                      placeholder={field.placeholder}
                      data-testid={`content-${field.key}`}
                      className="w-full bg-[#1A1A1A] border border-[#262626] text-[#EDEDED] px-4 py-2.5 rounded-lg text-sm focus:border-[#D4C5B0] focus:outline-none transition-colors"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {hasChanges && (
        <div className="mt-6 flex justify-end">
          <button onClick={save} disabled={saving} data-testid="save-content-btn-bottom"
            className="flex items-center gap-2 px-5 py-2.5 bg-[#D4C5B0] text-[#050505] text-sm font-medium rounded-lg hover:bg-[#c4b5a0] disabled:opacity-50 transition-colors">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      )}
    </div>
  );
}

// ── SEO Tab ──
function SeoRow({ project, onSaved }) {
  const [form, setForm] = useState({ seo_title: project.seo_title || "", seo_description: project.seo_description || "" });
  const [saving, setSaving] = useState(false);
  const dirty = form.seo_title !== (project.seo_title || "") || form.seo_description !== (project.seo_description || "");
  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/portfolio/${project.id}`, form);
      onSaved(res.data);
      toast.success("SEO saved");
    } catch { toast.error("Failed to save SEO"); }
    setSaving(false);
  };
  const inputCls = "w-full bg-[#1A1A1A] border border-[#262626] text-[#EDEDED] px-4 py-2.5 rounded-lg text-sm focus:border-[#D4C5B0] focus:outline-none transition-colors";
  return (
    <div data-testid={`seo-row-${project.id}`} className="bg-[#0E0E0E] border border-[#1A1A1A] rounded-xl p-5 flex gap-5">
      <img src={resolveImageUrl(project.image_url)} alt="" className="w-24 h-24 object-cover rounded-lg flex-shrink-0 hidden sm:block" />
      <div className="flex-1 space-y-3 min-w-0">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-[#EDEDED] text-sm font-semibold truncate">{project.title}</h3>
          <span className="text-[#555] text-[10px] uppercase tracking-wider">{project.category}</span>
        </div>
        <input value={form.seo_title} maxLength={70} onChange={(e) => setForm(f => ({ ...f, seo_title: e.target.value }))}
          placeholder={`Google title, e.g. ${project.title} – ${project.media_type === "video" ? "Videographer" : "Photographer"} Wellington`} data-testid={`seo-title-${project.id}`} className={inputCls} />
        <textarea value={form.seo_description} maxLength={160} rows={2} onChange={(e) => setForm(f => ({ ...f, seo_description: e.target.value }))}
          placeholder="Google description (max 160 characters). Mention the service and location." data-testid={`seo-desc-${project.id}`} className={`${inputCls} resize-none`} />
        <div className="flex items-center justify-between">
          <span className="text-[#555] text-[11px]">{form.seo_description.length}/160</span>
          {dirty && (
            <button onClick={save} disabled={saving} data-testid={`seo-save-${project.id}`}
              className="flex items-center gap-2 px-4 py-2 bg-[#D4C5B0] text-[#050505] text-xs font-medium rounded-lg hover:bg-[#c4b5a0] disabled:opacity-50 transition-colors">
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Save
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function SeoTab() {
  const [projects, setProjects] = useState(null);
  useEffect(() => { axios.get(`${API}/portfolio`).then(r => setProjects(r.data)); }, []);
  if (!projects) return <div className="text-center py-16 text-[#555]"><Loader2 className="w-8 h-8 mx-auto animate-spin" /></div>;
  const onSaved = (u) => setProjects(ps => ps.map(p => (p.id === u.id ? u : p)));
  return (
    <div className="space-y-4" data-testid="seo-tab">
      <p className="text-[#666] text-sm mb-2">Set how each project appears on Google. Leave empty to use the project title and description automatically.</p>
      {projects.map(p => <SeoRow key={p.id} project={p} onSaved={onSaved} />)}
    </div>
  );
}

// ── Main Admin ──
const tabs = [
  { id: "photos", label: "Photos", icon: Camera },
  { id: "videos", label: "Videos", icon: Film },
  { id: "testimonials", label: "Testimonials", icon: MessageSquare },
  { id: "site", label: "Site Images", icon: Image },
  { id: "content", label: "Content", icon: Pencil },
  { id: "seo", label: "SEO", icon: Search },
];

export default function Admin() {
  const [activeTab, setActiveTab] = useState("photos");

  return (
    <div data-testid="admin-page" className="pt-40 pb-16 min-h-screen bg-[#050505]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
          <div>
            <h1 className="text-3xl font-bold text-[#EDEDED]">Admin Panel</h1>
            <p className="text-[#555] text-sm mt-1">Manage portfolio, site content, images and videos</p>
          </div>
          <div className="flex gap-1 bg-[#0E0E0E] border border-[#1A1A1A] p-1 rounded-lg overflow-x-auto max-w-full">
            {tabs.map((tab) => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  activeTab === tab.id ? "bg-[#D4C5B0] text-[#050505]" : "text-[#666] hover:text-[#EDEDED]"
                }`}>
                <tab.icon size={15} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={activeTab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.15 }}>
            {activeTab === "photos" && <PhotosTab />}
            {activeTab === "videos" && <VideosTab />}
            {activeTab === "testimonials" && <TestimonialsTab />}
            {activeTab === "site" && <SiteImagesTab />}
            {activeTab === "content" && <ContentTab />}
            {activeTab === "seo" && <SeoTab />}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
